// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
// to do, and the count of files carrying it is the size of the refactor.
/**
 * SearchModal Context Provider
 * React equivalent of Angular's SearchModalService
 */

import React, { createContext, useState, useCallback, ReactNode } from 'react';
import { trackSearch } from '../../../utils/analytics';
import {
  SearchModalState,
  SearchModalConfig,
  SearchModalContextValue,
  StepStatus,
  ProgressStep,
} from './types';

const defaultProgressSteps: ProgressStep[] = [
  { icon: '🔍', title: 'URL Analysis & Validation', status: 'pending' },
  { icon: '🌐', title: 'Website Scanning & Data Extraction', status: 'pending' },
  { icon: '🏢', title: 'Brand Intelligence & Recognition', status: 'pending' },
  { icon: '📊', title: 'Final Processing & Report Generation', status: 'pending' },
];

const initialState: SearchModalState = {
  isVisible: false,
  progress: 0,
  currentStep: '',
  config: null,
  progressSteps: [],
};

export const SearchModalContext = createContext<SearchModalContextValue | undefined>(undefined);

interface SearchModalProviderProps {
  children: ReactNode;
}

export const SearchModalProvider: React.FC<SearchModalProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<SearchModalState>(initialState);

  const showModal = useCallback((config: SearchModalConfig) => {
    setModalState({
      isVisible: true,
      progress: 0,
      currentStep: 'Initializing analysis...',
      config: {
        animationType: 'card-based', // Default to card-based
        ...config,
      },
      progressSteps: [...defaultProgressSteps],
    });
  }, []);

  const hideModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isVisible: false,
    }));

    // Reset state after animation
    setTimeout(() => {
      setModalState(initialState);
    }, 300);
  }, []);

  const updateProgress = useCallback((step: string, progress: number) => {
    setModalState((prev) => ({
      ...prev,
      currentStep: step,
      progress: Math.min(100, Math.max(0, progress)),
    }));
  }, []);

  const updateStepStatus = useCallback((stepIndex: number, status: StepStatus) => {
    setModalState((prev) => {
      const newSteps = [...prev.progressSteps];
      if (newSteps[stepIndex]) {
        newSteps[stepIndex] = { ...newSteps[stepIndex], status };
      }
      return {
        ...prev,
        progressSteps: newSteps,
      };
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      config: prev.config
        ? { ...prev.config, isDarkMode: !prev.config.isDarkMode }
        : prev.config,
    }));
  }, []);

  const setTheme = useCallback((isDarkMode: boolean) => {
    setModalState((prev) => ({
      ...prev,
      config: prev.config ? { ...prev.config, isDarkMode } : prev.config,
    }));
  }, []);

  const startBrandAnalysis = useCallback((config: SearchModalConfig) => {
    if (config.url) trackSearch(config.url);
    showModal({
      title: '🎯 Brand Analysis in Progress',
      description: `We're performing comprehensive brand analysis on ${config.url}`,
      estimatedTime: 'Maximum processing time: 30 seconds',
      animationType: 'card-based',
      ...config,
    });
  }, [showModal]);

  const contextValue: SearchModalContextValue = {
    modalState,
    showModal,
    hideModal,
    updateProgress,
    updateStepStatus,
    toggleTheme,
    setTheme,
    startBrandAnalysis,
  };

  return (
    <SearchModalContext.Provider value={contextValue}>
      {children}
    </SearchModalContext.Provider>
  );
};